/**
 * Auth state: in-memory access token + persisted lightweight user info.
 * The refresh token lives only in an HttpOnly cookie set by /api/auth/login.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  userid: number;
  username: string;
  memberid: number;
}

export interface AppConfig {
  proforma_valid_days: number;
}

interface AuthState {
  access: string | null;
  user: AuthUser | null;
  config: AppConfig | null;
  setAccess: (token: string) => void;
  setSession: (token: string, user: AuthUser, config?: AppConfig | null) => void;
  setConfig: (config: AppConfig) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      access: null,
      user: null,
      config: null,
      setAccess: (access) => set({ access }),
      setSession: (access, user, config = null) =>
        set({ access, user, config: config ?? null }),
      setConfig: (config) => set({ config }),
      logout: () => set({ access: null, user: null, config: null }),
    }),
    {
      name: 'pos.auth',
      partialize: (state) => ({ user: state.user, config: state.config }),
    },
  ),
);

export function proformaValidDaysFromStore(): number {
  return useAuthStore.getState().config?.proforma_valid_days ?? 3;
}
